import {
  SampleAnnouncement,
  SampleSetAnnouncement,
} from "@/services/backend/deliveries/delivery/delivery";
import {
  AnnounceDeliveryRequest,
  useAnnounceDeliveryMutation,
} from "@/services/backend/deliveries/delivery/service";
import {
  DeliveryTemplate,
  SampleSetTemplate,
} from "@/services/backend/delivery-templates/delivery";
import {
  Testnorm,
  GrowthMedium,
  ProcedureType,
  SamplingMethod,
} from "@/services/backend/types";
import { AirSamplerSelector } from "@/shared/components/domain/air-samplers/air-sampler-selector";
import { CustomerFormSelect } from "@/shared/components/domain/customer/customer-form-select";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import DeliveryTemplateSelector from "@/shared/components/domain/templates/delivery-template-selector";
import {
  testnorm,
  procedureTypes,
  samplingMethods,
} from "@/shared/components/enum-labels";
import { Button } from "@/shared/components/ui/button";
import { Calendar } from "@/shared/components/ui/calendar";
import { Card, CardContent, CardFooter } from "@/shared/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Separator } from "@/shared/components/ui/separator";
import { Switch } from "@/shared/components/ui/switch";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import { cn } from "@/shared/lib/utils";
import { createEntityId } from "@/shared/nidavellir/types/entity-id";
import { formatDate } from "@/shared/utilities/date";
import { zodResolver } from "@hookform/resolvers/zod";
import { de } from "date-fns/locale";
import { CalendarIcon, Plus } from "lucide-react";
import { ReactElement, useState } from "react";
import { useFieldArray, useForm, useFormContext } from "react-hook-form";
import { z } from "zod";

// SampleSetAnnouncement Schema
const SampleSetAnnouncementSchema = z.object({
  id: z.string(),
  sampleSetNumber: z.string().min(1, "Eine Probensetnummer wird benötigt"),
  samplingMethod: z.nativeEnum(SamplingMethod),
  procedureType: z.nativeEnum(ProcedureType),
  comment: z.string().optional(),
});

// DeliveryAnnouncement Schema
const DeliveryAnnouncementSchema = z
  .object({
    id: z.string(),
    customerId: z.string(),
    trackingId: z.string().optional(),
    description: z.string().min(1, "Eine Beschreibung wird benötigt"),
    sampledBy: z.string(),
    sampleTakers: z.string({
      message: "Probenehmer werden benötigt",
    }),
    samplingVolume: z.string().optional(),
    testnorm: z.nativeEnum(Testnorm),
    airSamplerHoleCount: z.number().optional(),
    airSamplerDescription: z.string().optional(),
    airSamplerId: z.string().optional(),
    hasDifferentiation: z.boolean(),
    sampleDate: z.date({
      message: "Ein Beginn der Probenahme wird benötigt",
    }),
    sampleSets: z
      .array(SampleSetAnnouncementSchema)
      .min(1, "Mindestens ein Probenset muss enthalten sein"),
  })
  .superRefine((data, ctx) => {
    const isALK = data.sampleSets.some(
      (set) => set.samplingMethod === SamplingMethod.aLK,
    );
    if (isALK) {
      // Only validate required fields when ALK is selected in any sample set
      if (!data.airSamplerHoleCount) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Eine Lochzahl wird benötigt",
          path: ["airSamplerHoleCount"],
        });
      }
      if (!data.airSamplerDescription) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Eine Beschreibung für den Luftkeimsammler wird benötigt",
          path: ["airSamplerDescription"],
        });
      }
      if (!data.airSamplerId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Eine ID für den Luftkeimsammler wird benötigt",
          path: ["airSamplerId"],
        });
      }
      if (!data.samplingVolume) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Ein Probevolumen für den Luftkeimsammler wird benötigt",
          path: ["samplingVolume"],
        });
      }
    }
  });

function normalizeDeliveryTemplate(templateData: DeliveryTemplate): FormData {
  const firstSampleSet = templateData.sampleSets?.[0];
  const firstWithAirSampler = templateData.sampleSets?.find(
    (s) => s.airSampler,
  );

  // Für sampledBy, sampleTakers, testnorm: AirSampler zuerst, sonst erster SampleSet
  const sourceSet = firstWithAirSampler ?? firstSampleSet;
  const sampleDate = new Date();

  return {
    id: createEntityId()!.toString(),
    customerId: templateData.customerId,
    description: templateData.description,
    sampleSets:
      templateData.sampleSets?.map((s: SampleSetTemplate) => ({
        id: createEntityId()!.toString(),
        sampleSetNumber: s.sampleSetNumber ?? "",
        comment: s.comment ?? "",
        samplingMethod: s.samplingMethod ?? SamplingMethod.aLK,
        procedureType: s.procedureType ?? ProcedureType.RLT2N,
      })) ?? [],
    sampledBy: sourceSet?.sampledBy ?? "",
    sampleTakers: sourceSet?.sampleTakers?.join(", ") ?? "",
    testnorm: sourceSet?.testnorm as Testnorm,
    hasDifferentiation: sourceSet?.hasDifferentiation ?? false,
    samplingVolume:
      firstWithAirSampler?.samplingVolume?.toString() ?? undefined,
    airSamplerHoleCount:
      firstWithAirSampler?.airSampler?.holeCount ?? undefined,
    airSamplerDescription:
      firstWithAirSampler?.airSampler?.description ?? undefined,
    airSamplerId: firstWithAirSampler?.airSampler?.originalId ?? undefined,
    sampleDate,
  };
}

type FormData = z.infer<typeof DeliveryAnnouncementSchema>;

interface CreateDeliveryModalProps {
  className?: string;
}

export default function CreateDeliveryModal({
  className = "w-36",
}: CreateDeliveryModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [announceDelivery, { error }] = useAnnounceDeliveryMutation();
  const form = useForm<FormData>({
    resolver: zodResolver(DeliveryAnnouncementSchema),
    defaultValues: {
      id: createEntityId()!.toString(),
      description: "",
      trackingId: "",
      hasDifferentiation: false,
      sampleSets: [],
    },
  });
  const selectedCustomer = form.watch("customerId");

  const { handleSubmit, reset } = form;

  const createSamples = (
    procedure: ProcedureType,
    createdAt: string,
  ): SampleAnnouncement[] => {
    switch (procedure) {
      case ProcedureType.RLT2N: {
        return [
          {
            id: createEntityId()!.toString(),
            growthMedium: GrowthMedium.Caso,
            createdAt,
            description: "Caso",
          },
          {
            id: createEntityId()!.toString(),
            growthMedium: GrowthMedium.Malt,
            createdAt,
            description: "MEA",
          },
        ];
      }
      case ProcedureType.RLT1N: {
        return [
          {
            id: createEntityId()!.toString(),
            growthMedium: GrowthMedium.Caso,
            createdAt,
            description: "Caso",
          },
        ];
      }
      default: {
        return [];
      }
    }
  };

  const onSubmit = (data: FormData) => {
    const selectedDate = data.sampleDate.toISOString();
    const request: AnnounceDeliveryRequest = {
      id: data.id,
      customerId: data.customerId,
      description: data.description,
      trackingId: data.trackingId ? data.trackingId : undefined,
      sampleSets: data.sampleSets.map((set): SampleSetAnnouncement => {
        const newSet: SampleSetAnnouncement = {
          id: set.id,
          sampleSetNumber: set.sampleSetNumber,
          comment: set.comment ? set.comment : undefined,
          samplingMethod: set.samplingMethod,
          referenceId: data.id,
          procedureType: set.procedureType,
          sampledBy: data.sampledBy,
          samplingVolume: data.samplingVolume
            ? Number(data.samplingVolume)
            : undefined,
          testnorm: data.testnorm,
          sampleDate: selectedDate,
          sampleTakers: data.sampleTakers,
          samples: createSamples(set.procedureType, selectedDate),
          hasDifferentiation:
            set.samplingMethod === SamplingMethod.aLK
              ? data.hasDifferentiation
              : false,
        };

        // Only include airSampler data when ALK sampling method is used
        if (set.samplingMethod === SamplingMethod.aLK) {
          newSet.airSampler = {
            id: data.airSamplerId!,
            originalId: data.airSamplerId!,
            description: data.airSamplerDescription!,
            holeCount: data.airSamplerHoleCount!,
          };
        }

        return newSet;
      }),
    };

    announceDelivery(request);
    setIsOpen(false);
    reset({
      id: createEntityId()!.toString(), // Generate new ID
      customerId: "", // Explicitly reset customerId
      description: "",
      trackingId: "",
      hasDifferentiation: false,
      sampleSets: [], // Reset arrays
    });
  };

  const prefillWithTemplate = (templateData: DeliveryTemplate): void => {
    if (!templateData) return;
    const normalized = normalizeDeliveryTemplate(templateData);
    const parsed = DeliveryAnnouncementSchema.parse(normalized);
    form.reset(parsed);
  };

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger className={cn(className)} asChild>
        <Button variant="default">Lieferung anlegen</Button>
      </DialogTrigger>
      <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-[425px] md:max-w-[700px] lg:max-w-[900px]">
        <DialogHeader>
          <DialogTitle>Lieferung anlegen</DialogTitle>
          <DialogDescription>
            Hier können neue Lieferungen angemeldet werden, welche nicht durch
            ein angebundenes System automatisch erstellt werden.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-1 flex-col"
          >
            <div className="mb-3 flex-1 space-y-2 overflow-hidden">
              <CustomerFormSelect name="customerId" control={form.control} />
              {selectedCustomer && (
                <DeliveryTemplateSelector
                  customerId={selectedCustomer}
                  setDeliveryTemplate={(template) =>
                    prefillWithTemplate(template)
                  }
                />
              )}
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Beschreibung</FormLabel>
                      <FormControl>
                        <Input placeholder="Beschreibung" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="trackingId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tracking-Id</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Tracking-Id (optional)"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <Tabs
                defaultValue="samples"
                className="w-full flex-1 overflow-hidden"
              >
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="samples">Proben</TabsTrigger>
                  <TabsTrigger value="metadata">Probenahmedaten</TabsTrigger>
                  <TabsTrigger value="active-air-microbial-metadata">
                    aLK
                  </TabsTrigger>
                </TabsList>
                <TabsContent
                  value="samples"
                  className="mt-2 flex-1 overflow-hidden"
                >
                  <SampleSetsTab />
                </TabsContent>
                <TabsContent
                  value="metadata"
                  className="mt-2 flex-1 overflow-hidden"
                >
                  <SamplingMetadata />
                </TabsContent>
                <TabsContent
                  value="active-air-microbial-metadata"
                  className="mt-2 flex-1 overflow-hidden"
                >
                  <ActiveMicrobialMetadataTab />
                </TabsContent>
              </Tabs>
            </div>
            {/* Buttons */}
            <div className="flex justify-between space-x-3 pt-3">
              <Button type="submit">Lieferung speichern</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function SampleSetsTab(): ReactElement {
  const methods = useFormContext();
  const {
    fields: sampleSetFields,
    append,
    remove,
  } = useFieldArray({
    control: methods.control,
    name: "sampleSets",
  });

  return (
    <Card className="flex-1 border-0 p-0 shadow-none">
      <CardContent className="h-full p-0">
        <ScrollArea className="h-[300px] p-2 md:h-[300px]">
          {sampleSetFields.map((sampleSet, sampleSetIndex) => (
            <Card key={`${sampleSet.id}`} className="mb-2">
              <CardContent className="h-full p-3">
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  <FormField
                    control={methods.control}
                    name={`sampleSets.${sampleSetIndex}.sampleSetNumber`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Probensetnummer</FormLabel>
                        <FormControl>
                          <Input placeholder="Probensetnummer" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={methods.control}
                    name={`sampleSets.${sampleSetIndex}.comment`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Kommentar</FormLabel>
                        <FormControl>
                          <Input placeholder="Kommentar" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={methods.control}
                    name={`sampleSets.${sampleSetIndex}.samplingMethod`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Methode</FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger
                                className="w-full"
                                defaultValue={field.value || SamplingMethod.aLK}
                              >
                                <SelectValue placeholder="Probentyp auswählen" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent
                              defaultValue={field.value || SamplingMethod.aLK}
                            >
                              {Object.values(SamplingMethod)
                                .filter(
                                  (method) => method !== SamplingMethod.pLK,
                                )
                                .map((value) => (
                                  <SelectItem key={value} value={value}>
                                    {samplingMethods[value]}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={methods.control}
                    name={`sampleSets.${sampleSetIndex}.procedureType`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Verfahren</FormLabel>
                        <FormControl>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger
                                className="w-full"
                                defaultValue={
                                  field.value || ProcedureType.RLT2N
                                }
                              >
                                <SelectValue placeholder="Verfahren auswählen" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent
                              defaultValue={field.value || ProcedureType.RLT2N}
                            >
                              {Object.values(ProcedureType).map((value) => (
                                <SelectItem key={value} value={value}>
                                  {procedureTypes[value]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <Button
                  className="mt-2"
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(sampleSetIndex);
                  }}
                >
                  Löschen
                </Button>
              </CardContent>
            </Card>
          ))}
        </ScrollArea>
      </CardContent>
      <CardFooter className="flex flex-col items-start space-y-2 pt-2">
        <Separator />
        <Button
          className="w-full"
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            append({
              id: createEntityId()!.toString(),
              sampleSetNumber: "",
              samplingMethod: SamplingMethod.aLK,
              procedureType: ProcedureType.RLT2N,
              comment: "",
            })
          }
        >
          <Plus className="mr-1 h-3 w-3" /> Probenset hinzufügen
        </Button>
      </CardFooter>
    </Card>
  );
}

function SamplingMetadata(): ReactElement {
  const methods = useFormContext();
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  return (
    <ScrollArea className="h-[300px] md:h-[300px]">
      <Card className="py-0">
        <CardContent className="flex flex-col space-y-3 p-3">
          <FormField
            control={methods.control}
            name="sampledBy"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Beprobung durch:</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Beprobung durch..."
                    type="text"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={methods.control}
            name="sampleTakers"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Probenehmer</FormLabel>
                <FormControl>
                  <Input placeholder="Probenehmer" type="text" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={methods.control}
            name="testnorm"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Prüfgrundlage</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger
                        className="w-full"
                        defaultValue={field.value || Testnorm.VDI}
                      >
                        <SelectValue placeholder="Prüfgrundlage" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent defaultValue={field.value || Testnorm.VDI}>
                      {Object.values(Testnorm).map((value) => (
                        <SelectItem key={value} value={value}>
                          {testnorm[value]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={methods.control}
            name="sampleDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Beginn der Probenahme</FormLabel>
                <Popover
                  open={isDatePickerOpen}
                  onOpenChange={setIsDatePickerOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      data-empty={!field.value}
                      className="data-[empty=true]:text-muted-foreground justify-start text-left font-normal"
                    >
                      <CalendarIcon />
                      {field.value ? (
                        formatDate(field.value.toUTCString(), de)
                      ) : (
                        <span>Datum auswählen</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(date) => {
                        field.onChange(date);
                        setIsDatePickerOpen(false);
                      }}
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>
    </ScrollArea>
  );
}

function ActiveMicrobialMetadataTab(): ReactElement {
  const methods = useFormContext();
  return (
    <ScrollArea className="h-[300px] md:h-[300px]">
      <Card className="py-0">
        <CardContent className="flex flex-col space-y-3 p-3">
          <AirSamplerSelector
            onChange={(airSampler) => {
              methods.setValue("airSamplerHoleCount", airSampler.holeCount);
              methods.setValue("airSamplerDescription", airSampler.description);
              methods.setValue("airSamplerId", airSampler.originalId);
            }}
          />
          <FormField
            control={methods.control}
            name="airSamplerDescription"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Beschreibung</FormLabel>
                <FormControl>
                  <Input
                    disabled
                    placeholder="Beschreibung"
                    type="text"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={methods.control}
            name="airSamplerHoleCount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Lochzahl</FormLabel>
                <FormControl>
                  <Input
                    disabled
                    placeholder="Lochzahl"
                    type="number"
                    min={0}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={methods.control}
            name="samplingVolume"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Probevolumen</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Probevolumen"
                    type="number"
                    min={0}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={methods.control}
            name="hasDifferentiation"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                <FormLabel className="font-normal">Differenzierung</FormLabel>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </CardContent>
      </Card>
    </ScrollArea>
  );
}
